import React, { useEffect, useState, useCallback } from 'react';
import { useAxios } from '../../../Helpers/AxiosHelper';
import { useToast } from '../../../Helpers/ToastHelper';
import { useTranslation } from 'react-i18next';
import { Column, DataTable, DDContactType, Dialog, InputText, SCButton, SCIcon } from '../../../Common/Imports';
import { ApiResponse, Contacts_EXT } from '../../../Models/Classes';
import { ContactType } from '../../../Models/Enums';

interface OperativeContactsProps {
    IDReservation: number;
    IDProperty: number;
    ShowPropertyContacts?: boolean;
    ShowReservationContacts?: boolean;
    OnHide?: () => void;
}

export const OperativeContacts: React.FC<OperativeContactsProps> = ({ IDReservation, IDProperty, ShowPropertyContacts = false, ShowReservationContacts = false, OnHide }) => {
    const { t } = useTranslation();
    const [PropertyContacts, setPropertyContacts] = useState<Contacts_EXT[]>([]);
    const [ReservationContacts, setReservationContacts] = useState<Contacts_EXT[]>([]);
    const [visible, setVisible] = useState<boolean>(false);
    const [NewContactForReservationName, setNewContactForReservationName] = useState<string>("");
    const [NewContactForReservationType, setNewContactForReservationType] = useState<number>();
    const [NewContactForPropertyName, setNewContactForPropertyName] = useState<string>("");
    const [NewContactForPropertyType, setNewContactForPropertyType] = useState<number | undefined>(1);
    const AxiosHelper = useAxios();
    const { showToast } = useToast();

    const GetData = useCallback(async () => {

        try {
            const resp = await AxiosHelper.get<ApiResponse<Contacts_EXT[]>>(`Contacts/GetPropertyContactsList/${IDProperty}`);
            if (resp.data.code === 0) {
                setPropertyContacts(resp.data.data);
                setVisible(true);
            }
        } catch (error) {
            console.error('Error fetching property contacts:', error);
        }

        try {
            const resp = await AxiosHelper.get<ApiResponse<Contacts_EXT[]>>(`Contacts/GetReservationContactsList/${IDReservation}`);
            if (resp.data.code === 0) {
                setReservationContacts(resp.data.data);
                setVisible(true);
            }
        } catch (error) {
            console.error('Error fetching reservation contacts:', error);
        }
    }, [AxiosHelper, IDProperty, IDReservation]);

    useEffect(() => {
        GetData();
    }, [GetData]);

    const propertyContactTypeBody = (data: Contacts_EXT) => {
        if (data.type === undefined) {
            return null;
        }
        return <SCIcon Name={ContactType[data.type]} />
    }

    const changeFavorite = async (data: Contacts_EXT) => {
        const _Data = { ...data };
        _Data.favorite = _Data.favorite ? !_Data.favorite : true;
        _Data.blacklist = false;
        try {
            const resp = await AxiosHelper.post<ApiResponse<Contacts_EXT[]>>(`Contacts/UpdateContact`, _Data);
            if (resp.data.code === 0) {
                GetData()
            }
        } catch (error) {
            console.error('Error fetching property contacts:', error);
        }
    }

    const favoriteBody = (data: Contacts_EXT) => {
        return <>
            <SCIcon
                Name="Favorite"
                Filled={data.favorite == true}
                Pointer OnClick={() => changeFavorite(data)} />
        </>
    }

    const changeBlacklist = async (data: Contacts_EXT) => {
        const _Data = { ...data };
        _Data.blacklist = _Data.blacklist ? !_Data.blacklist : true;
        _Data.favorite = false;
        try {
            const resp = await AxiosHelper.post<ApiResponse<Contacts_EXT[]>>(`Contacts/UpdateContact`, _Data);
            if (resp.data.code === 0) {
                GetData()
            }
        } catch (error) {
            console.error('Error fetching property contacts:', error);
        }
    }

    const blacklistBody = (data: Contacts_EXT) => {
        return <>
            <SCIcon
                Name="Blacklist"
                Filled={data.blacklist == true}
                Pointer OnClick={() => changeBlacklist(data)} />
        </>
    }


    const createContactForReservation = async () => {
        if (!NewContactForReservationName || NewContactForReservationType == undefined) {
            showToast('error', 'Error', t("Operative.Contacts.MissingFields"));
            return;
        }
        const _Contact: Contacts_EXT = {
            value: NewContactForReservationName,
            type: NewContactForReservationType,
            idReservation: IDReservation
        }
        if (_Contact) {
            try {
                const resp = await AxiosHelper.post<ApiResponse<Contacts_EXT[]>>(`Contacts/AddContact`, _Contact);
                if (resp.data.code === 0) {
                    GetData()
                }
            } catch (error) {
                console.error('Error fetching property contacts:', error);
            }
        }
    }

    const createContactForProperty = async () => {
        if (!NewContactForPropertyName || NewContactForPropertyType == undefined) {
            showToast('error', 'Error', t("Operative.Contacts.MissingFields"));
            return;
        }
        const _Contact: Contacts_EXT = {
            value: NewContactForPropertyName,
            type: NewContactForPropertyType,
            idPropertyInfo: IDProperty
        }

        if (_Contact) {
            try {
                const resp = await AxiosHelper.post<ApiResponse<Contacts_EXT[]>>(`Contacts/AddContact`, _Contact);
                if (resp.data.code === 0) {
                    GetData()
                }
            } catch (error) {
                console.error('Error fetching property contacts:', error);
            }
        }
    }

    return (
        (ShowPropertyContacts || ShowReservationContacts) &&
        <Dialog header="Contacts" visible={visible} onHide={() => { setVisible(false); OnHide?.() }}>
            {
                ShowPropertyContacts &&
                <>
                    <div className="row">
                        <div className="col-md-12">
                            <h5>{t("Property Contacts")}</h5>
                        </div>
                    </div>
                    <div className="row mb-2">
                        <div className="col-md-12">
                            <div className="p-inputgroup flex-1">
                                <InputText value={NewContactForPropertyName} onChange={(e) => setNewContactForPropertyName(e.target.value)} />
                                <DDContactType onSelect={setNewContactForPropertyType} />
                                <SCButton Name="Add" OnClick={createContactForProperty}></SCButton>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12">
                            <DataTable
                                paginator
                                rows={10}
                                rowsPerPageOptions={[10, 20, 50]}
                                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
                                paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                value={PropertyContacts}
                                size="small"
                                stripedRows
                                tableStyle={{ minWidth: '50rem' }}>
                                <Column
                                    field="type"
                                    body={propertyContactTypeBody}
                                    style={{ width: '50px' }}
                                    bodyClassName="table-cell-nowrap" />
                                <Column
                                    field="value"
                                    bodyClassName="table-cell-nowrap" />
                                <Column
                                    body={favoriteBody}
                                    style={{ width: '50px' }}
                                    bodyClassName="table-cell-nowrap" />
                                <Column
                                    body={blacklistBody}
                                    style={{ width: '50px' }}
                                    bodyClassName="table-cell-nowrap" />
                            </DataTable>
                        </div>
                    </div>
                </>
            }
            {
                ShowReservationContacts &&
                <>
                    <div className="row">
                        <div className="col">
                            <h5>{t("Reservation Contacts")}</h5>
                            <div className="row mb-2">
                                <div className="col-md-12">
                                    <div className="p-inputgroup flex-1">
                                        <InputText value={NewContactForReservationName} onChange={(e) => setNewContactForReservationName(e.target.value)} />
                                        <DDContactType onSelect={setNewContactForReservationType} />
                                        <SCButton Name="Add" OnClick={createContactForReservation}></SCButton>
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-12">
                                    <DataTable
                                        paginator
                                        rows={10}
                                        rowsPerPageOptions={[10, 20, 50]}
                                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
                                        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                        value={ReservationContacts}
                                        size="small"
                                        stripedRows
                                        tableStyle={{ minWidth: '50rem' }}>
                                        <Column
                                            field="type"
                                            body={propertyContactTypeBody}
                                            style={{ width: '50px' }}
                                            bodyClassName="table-cell-nowrap" />
                                        <Column
                                            field="value"
                                            bodyClassName="table-cell-nowrap" />
                                        <Column
                                            body={favoriteBody}
                                            style={{ width: '50px' }}
                                            bodyClassName="table-cell-nowrap" />
                                        <Column
                                            body={blacklistBody}
                                            style={{ width: '50px' }}
                                            bodyClassName="table-cell-nowrap" />
                                    </DataTable>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            }
        </Dialog >
    );
};

export default OperativeContacts;
